import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext, Store } from '@ngxs/store';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../../../common/services/notification/notification.service';
import {
  HubLoanerPhoneItemCreate,
  HubLoanerPhoneItemDelete,
  HubLoanerPhoneItemInitialState,
  HubLoanerPhoneItemLoad,
  HubLoanerPhoneItemReturn,
  HubLoanerPhoneItemUpdate,
} from './hub-loaner-phone-item.actions';
import { HubLoanerPhoneItemService } from './hub-loaner-phone-item.service';
import { HubLoanerPhoneListItem } from '../../../models';
import { NavigateUserService } from '../../../../../../services/navigate-user.service';
import { Error, CustomHttpErrorResponse } from '../../../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface HubLoanerPhoneItemStateModel {
  loanerPhoneItem: HubLoanerPhoneListItem;
  loaded: boolean;
}

const initialState: HubLoanerPhoneItemStateModel = {
  loanerPhoneItem: null,
  loaded: false,
};

@State<HubLoanerPhoneItemStateModel>({
  name: 'hubLoanerPhoneItem',
  defaults: initialState,
})
@Injectable()
export class HubLoanerPhoneItemState {
  constructor(
    private hubLoanerPhoneItemService: HubLoanerPhoneItemService,
    private notificationService: NotificationService,
    private store: Store,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getState(state: HubLoanerPhoneItemStateModel): HubLoanerPhoneItemStateModel {
    return state;
  }

  @Selector()
  static getLoanerPhoneItem(state: HubLoanerPhoneItemStateModel): HubLoanerPhoneListItem {
    return state.loanerPhoneItem;
  }

  @Selector()
  static getLoaded(state: HubLoanerPhoneItemStateModel): boolean {
    return state.loaded;
  }

  @Action(HubLoanerPhoneItemLoad)
  getHubLoanerPhoneItem(
    ctx: StateContext<HubLoanerPhoneItemStateModel>,
    { payload: { hubId, id } }: HubLoanerPhoneItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubLoanerPhoneItemService.getLoanerPhoneItem(hubId, id).pipe(
      take(1),
      map((loanerPhoneItem: HubLoanerPhoneListItem) => {
        ctx.patchState({ loanerPhoneItem, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubLoanerPhoneItemReturn)
  returnLoanerPhoneItem(
    ctx: StateContext<HubLoanerPhoneItemStateModel>,
    { payload: { dialog, hubId, id, returnTimestamp } }: HubLoanerPhoneItemReturn,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubLoanerPhoneItemService.returnLoanerPhoneItem(hubId, id, returnTimestamp).pipe(
      map(() => {
        dialog.close();
        this.notificationService.success('api.loanerPhone.success.return');
        this.store.dispatch(new HubLoanerPhoneItemLoad({ hubId, id }));
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubLoanerPhoneItemCreate)
  createLoanerPhoneItem(
    ctx: StateContext<HubLoanerPhoneItemStateModel>,
    { payload: { hubId, loanerPhoneItem } }: HubLoanerPhoneItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubLoanerPhoneItemService.createLoanerPhoneItem(hubId, loanerPhoneItem).pipe(
      map(() => {
        this.notificationService.success('api.loanerPhone.success.create');
        this.navigateUserService.navigateUserToAbsolutePath(`/dashboard/administration/hubs/${hubId}/loaner-phones`);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubLoanerPhoneItemUpdate)
  updateLoanerPhoneItem(
    ctx: StateContext<HubLoanerPhoneItemStateModel>,
    { payload: { hubId, loanerPhoneItem } }: HubLoanerPhoneItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubLoanerPhoneItemService.updateLoanerPhoneItem(hubId, loanerPhoneItem).pipe(
      map(() => {
        this.notificationService.success('api.loanerPhone.success.update');
        this.navigateUserService.navigateUserToAbsolutePath(`/dashboard/administration/hubs/${hubId}/loaner-phones`);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubLoanerPhoneItemDelete)
  deleteLoanerPhoneItem(
    ctx: StateContext<HubLoanerPhoneItemStateModel>,
    { payload: { dialog, hubId, id } }: HubLoanerPhoneItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubLoanerPhoneItemService.deleteLoanerPhoneItem(hubId, id).pipe(
      map(() => {
        dialog.close();
        this.notificationService.success('api.loanerPhone.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath(`/dashboard/administration/hubs/${hubId}/loaner-phones`);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubLoanerPhoneItemInitialState)
  setInitialState(ctx: StateContext<HubLoanerPhoneItemStateModel>): void {
    ctx.setState(initialState);
  }
}
